<template>
  <div
    :class="contributorClasses"
    :data-cube="this.cube.cubeName"
    :id="`c_${this.cube.cubeName}`"
  >
    <a class="contributor__left" @click="showInMap()">
      <img
        class="contributor__img"
        :src="cubeIconUrl(this.cube, 'svg')"
      />
      <h4 class="contributor__title">{{ nickName }}</h4>
    </a>
    <div class="contributor__content">
      <div class="contributor__content-top">
        <div class="contributor__priority">
          <div class="contributor__priority-title">{{ t('efriends.priority') }}</div>
          <Choice
            name="priority"
            :default="priority"
            :items="priorities"
            :use-select="true"
            @selection-changed="onPriorityChanged"
            ref="connection-priority"
          />
        </div>
      </div>
      <div class="contributor__content-bottom"></div>
      <div :class="submitClasses">
        <a class="contributor__cancel" @click="cancel()">{{ t('global.cancel') }}</a>

        <LinkButton
          type="small"
          icon-right="Arrow"
          :extra-classes="saveButtonClasses"
          :text="t('global.save')"
          @click.native="saveChanges()"
        />
      </div>
    </div>
  </div>
</template>

<script>
  import Choice from '@/components/Choice.vue'
  import LinkButton from '@/components/LinkButton.vue'
  import { useStrings } from '@/composables/useStrings.ts'
  import { CONNECTION_PRIORITIES } from '@/constants'

  import { useFormsStore } from '@/stores/useFormsStore'
  import { useMapStore } from '@/stores/useMapStore'
  import { useCubeStore } from '@/stores/useCubeStore'
  import { useTemplateRef } from 'vue'
  import { useRouter, useRoute } from 'vue-router'

  export default {
    name: 'contributor',
    setup() {
      const router = useRouter()
      const currentRoute = useRoute()
      const formsStore = useFormsStore()
      const cubeStore = useCubeStore()
      const { t } = useStrings()
      const { cubeIconUrl } = useMapStore()
      const connectionPriorityRef = useTemplateRef('connection-priority')

      return {
        t,
        cubeIconUrl,
        cubeStore,
        formsStore,
        router,
        currentRoute,
        connectionPriority: connectionPriorityRef,
      }
    },
    props: {
      cube: {
        type: Object,
        required: true,
      },
    },
    components: {
      Choice,
      LinkButton,
    },
    data() {
      return {
        originalPriority: 'default',
        priority: '',
        bodyOnClickHandler: false,
        disconnecting: false,
      }
    },
    // We need to use created here to preserve state correctly.
    created() {
      this.initializeValues()
    },
    computed: {
      contributorClasses() {
        let result = ['contributor']
        const cubeNameRegex = new RegExp(`c_${this.cube.cubeName}`)
        if (
          this.currentRoute.name === 'efriends' &&
          this.currentRoute.hash.match(cubeNameRegex)
        ) {
          result.push('contributor--highlighted')
        }
        return result.join(' ')
      },
      priorities: function () {
        return CONNECTION_PRIORITIES
      },
      nickName: function () {
        return this.cube.params.nickName
      },
      submitClasses: function () {
        let classes = ['contributor__submit-buttons']
        if (this.isChanged()) {
          classes.push('contributor__submit-buttons--expanded')
        }
        return classes.join(' ')
      },
      saveButtonClasses: function () {
        let classes = ['button--primary contributor__save']
        if (
          this.formsStore.isFormSubmitting(
            `producers.${this.cube.cubeName}`
          )
        ) {
          classes.push('contributor__actions--submitting')
        }
        return classes
      },
    },
    methods: {
      initializeValues: function () {
        this.priority = this.cubeStore.connectionPriorityFromCube(this.cube)
        this.originalPriority = this.priority
      },
      showInMap: function () {
        this.router.push({
          name: 'map',
          params: {
            selected: this.cube.cubeName,
          },
        })
      },
      onPriorityChanged: function (newPriority) {
        this.priority = newPriority
      },
      stopDonating() {
        this.disconnecting = true
        this.cubeStore.stopDonatingToCube(this.cube)
      },
      isChanged: function () {
        return this.priority !== this.originalPriority
      },
      cancel: function () {
        this.priority = this.originalPriority
        this.connectionPriority.showSelect = false
      },
      saveChanges: async function () {
        this.cubeStore.setConnectionPriority({
          cubeName: this.cube.cubeName,
          priority: this.priority,
          forConsumers: false,
        })
        this.originalPriority = this.priority
        this.connectionPriority.showSelect = false
      },
    },
  }
</script>

<style lang="scss" scoped>
  $producer-padding-top: $spacer10;

  .contributor {
    padding: $spacer10 0;
    position: relative;
    display: flex;
    flex-wrap: wrap;

    &--highlighted {
      background: $grey-lightest;
    }

    &__left {
      margin-right: $spacer10;
      text-align: center;
      transition: $transition-base;
      width: $cube-list-img-size-min;
      flex-shrink: 0;
      cursor: pointer;
      position: relative;

      @media (min-width: $bp-xs) {
        width: $cube-list-img-size-xs;
      }

      @media (min-width: $bp-sm) {
        flex-basis: $efriends-page-icon-width-sm;
      }
    }

    &__img {
      padding: 0;
      max-width: 100%;

      @media (min-width: $bp-ssm) {
        padding: 0 $spacer05;
      }

      @media (min-width: $bp-sm) {
        padding: 0 $spacer20;
      }
    }

    &__title {
      margin: 0;
      text-align: center;
      font-size: $font-size-sm;
      line-height: $line-height-min;
      transition: $transition-base;
      font-weight: 500;

      @media (min-width: $bp-ssm) {
        font-size: $font-size-base;
      }
    }

    &__label {
      font-size: $font-size-sm;
      color: $grey-dark;

      @media (min-width: $bp-xs) {
        font-size: $font-size-base;
      }
    }

    &__priority {
      margin-right: $spacer05;

      &-title {
        font-size: $font-size-sm;
        color: $grey-dark;
        line-height: $line-height-sm;
      }
    }

    &__content {
      flex-grow: 1;

      @media (min-width: $bp-md) {
        display: flex;
        flex-wrap: wrap;
        // Only when setting a width that is smaller than the available space
        // this will fit onto the same line as left.
        width: 30%;
      }

      &-top {
        display: flex;
        flex-wrap: wrap;
      }

      &-bottom {
        margin-top: $spacer10;
        display: flex;
        justify-self: flex-end;
        margin-left: auto;
        flex-grow: 1;
        flex-basis: 40%;

        @media (min-width: $bp-xs) {
          justify-content: flex-end;
        }

        @media (min-width: $bp-md) {
          margin-top: 0;
        }
      }
    }

    &__toggle-actions {
      color: $grey-light;
      cursor: pointer;
      transition: $transition-base;
      width: $spacer20;
      padding: 0 $spacer05;
      align-self: flex-end;
      position: absolute;
      top: $producer-padding-top + 1.15rem;
      right: 0;

      @include hover-focus-active {
        color: $community;
      }

      &--active {
        color: $community;
      }

      .icon {
        width: $spacer05;
      }
    }

    &__actions {
      position: absolute;
      right: 0;
      top: 100%;
      left: auto;
      overflow: hidden;
      transition: opacity 0.3s ease 0s, visibility 0s linear 0.5s;
      padding: $spacer05 $spacer10;
      border: 1px solid $grey-light;
      border-radius: $border-radius;
      opacity: 0;
      width: 200px;
      background: $white;
      visibility: hidden;

      @media (min-width: $bp-sm) {
        right: $spacer05;
      }

      &--visible {
        visibility: visible;
        transition: visibility 0s linear 0s, opacity 0.3s ease 0.1s;
        opacity: 1;
        box-shadow: 2px 2px 3px rgba(0, 0, 0, 0.1);
      }

      &-overlay {
        position: fixed;
        left: 0;
        right: 0;
        top: 0;
        bottom: 0;
        width: 100%;
        height: 100%;
        z-index: 1;
      }
    }

    &__action-disconnect {
      cursor: pointer;
      transition: $transition-base;

      @include hover-focus-active {
        color: $error-color;
      }

      .icon {
        margin-right: $spacer025;
        width: 0.7rem;
        vertical-align: middle;
      }

      &--disconnecting {
        .icon {
          animation: spin 2s linear 0s infinite;
        }
      }
    }

    &__submit-buttons {
      max-height: 0;
      overflow: hidden;
      transition: $transition-base;
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-top: $spacer05;
      // The __right element has a margin-right for producers to accomodate for
      // the toggle actions button. We still want the submit buttons to be at
      // the very right though.
      margin-right: $spacer20 * -1;

      @media (min-width: $bp-md) {
        justify-content: flex-end;
        flex-basis: 100%;
      }

      &--expanded {
        max-height: 60px;
      }
    }

    &__cancel {
      cursor: pointer;
      transition: $transition-base;
    }

    &__save {
      margin-left: $spacer10;
    }
  }
</style>
